import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import MotionPhotosAutoIcon from "@mui/icons-material/MotionPhotosAuto";
import { useNavigate } from "react-router-dom";
import { OrangeTextButton } from "../../../common/OrangeTextButton";
import { handleSetMilestoneMotion } from "./SiteAboutPageUtils/handleSetMilestoneMotion";
import {
  AlertState,
  SiteCameras,
} from "./SiteAboutPageUtils/siteAboutInterfaces";

type Dispatcher<S> = React.Dispatch<React.SetStateAction<S>>;

export type MilestoneMotionScope = "site" | "selected";

/** Only the generic (XProtect) Milestone integration has the per-camera flag. */
export const showMilestoneMotionButton = (
  integrationTypeName?: string | null,
): boolean => integrationTypeName === "milestone";

/**
 * Site About → cameras toolbar: turn Milestone motion on/off for every camera
 * on the site (or just the checked ones). ENG-950 / SUP-133.
 */
export const MilestoneMotionButton = ({
  selected,
  siteInfo,
  setSiteCameras,
  setAlertState,
  setSelected,
  setSyncNeeded,
  setBackdropLoadingScreenOn,
}: {
  selected: readonly string[];
  siteInfo: any;
  setSiteCameras: Dispatcher<SiteCameras>;
  setAlertState: Dispatcher<AlertState>;
  setSelected: Dispatcher<readonly string[]>;
  setSyncNeeded: Dispatcher<boolean>;
  setBackdropLoadingScreenOn: Dispatcher<boolean>;
}) => {
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const [scope, setScope] = React.useState<MilestoneMotionScope>("site");

  const selectedCount = selected.length;

  const openDialog = () => {
    setScope(selectedCount > 0 ? "selected" : "site");
    setOpen(true);
  };

  const apply = (enabled: boolean) => {
    setOpen(false);
    setBackdropLoadingScreenOn(true);
    handleSetMilestoneMotion({
      siteId: siteInfo.id,
      enabled,
      cameraIds:
        scope === "selected" ? selected.map((id) => Number(id)) : undefined,
      navigate,
      setSiteCameras,
      setAlertState,
      setSelected,
      setSyncNeeded,
      setBackdropLoadingScreenOn,
    });
  };

  return (
    <>
      <OrangeTextButton
        disabled={false}
        onClick={openDialog}
        text={
          <>
            <MotionPhotosAutoIcon fontSize="small" />
            &nbsp;MILESTONE MOTION
          </>
        }
      />
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby="milestone-motion-dialog-title"
      >
        <DialogTitle
          id="milestone-motion-dialog-title"
          sx={{ fontFamily: "Mulish" }}
        >
          Milestone motion
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontFamily: "Mulish" }}>
            When on, frames are pulled from Milestone only while it reports
            motion. That cuts bandwidth and load substantially, but detection
            then depends on Milestone&apos;s motion: if it is tuned too tightly,
            events reach us late or not at all — and that shows up as a missed
            detection. It can also add load to the recording server, depending
            on whether Milestone analyses the video itself or reads the
            cameras&apos; own motion events. Best used where Milestone motion is
            known to be well configured.
          </DialogContentText>
          <DialogContentText sx={{ fontFamily: "Mulish", mt: 2 }}>
            When off, frames are pulled continuously. New cameras added to this
            site will follow whichever setting most cameras have. The change is
            applied on the next Sync.
          </DialogContentText>
          <FormControl sx={{ mt: 2 }}>
            <RadioGroup
              aria-label="Apply to"
              value={scope}
              onChange={(event) =>
                setScope(event.target.value as MilestoneMotionScope)
              }
            >
              <FormControlLabel
                value="site"
                control={<Radio />}
                label="All cameras on this site"
              />
              <FormControlLabel
                value="selected"
                control={<Radio />}
                disabled={selectedCount === 0}
                label={`Selected cameras (${selectedCount})`}
              />
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <OrangeTextButton
            disabled={false}
            onClick={() => setOpen(false)}
            text="Cancel"
          />
          <OrangeTextButton
            disabled={false}
            onClick={() => apply(false)}
            text="Turn off"
          />
          <OrangeTextButton
            disabled={false}
            onClick={() => apply(true)}
            text="Turn on"
          />
        </DialogActions>
      </Dialog>
    </>
  );
};
